import { makeStyles } from "tss-react/mui"
import { Box, ButtonBase, Typography } from "@mui/material"
import type { DateTime } from "luxon"

import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"

import { DatePickerButton } from "./DatePickerButton"

type DayPillStyleProps = Readonly<{ isSelected: boolean }>

const useDayPillStyles = makeStyles<DayPillStyleProps>()((theme, { isSelected }) => ({
    pill: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: ".625rem .875rem",
        borderRadius: ".75rem",
        background: isSelected ? theme.palette.tailwind.slate[900] : "transparent",
        cursor: "pointer",
        minWidth: "3.75rem",
        transition: "background 0.15s",
        flexShrink: 0,
        gap: ".125rem",
        "&:hover": {
            background: isSelected ? theme.palette.tailwind.slate[800] : theme.palette.tailwind.slate[200],
        },
    },
    dayNumber: {
        color: isSelected ? theme.palette.common.white : theme.palette.text.primary,
        fontWeight: 600,
        lineHeight: 1,
        marginBottom: ".25rem",
    },
    dayName: {
        color: isSelected ? theme.palette.tailwind.slate[300] : theme.palette.text.secondary,
        lineHeight: 1,
    },
}))

type DayPillProps = Readonly<{
    day: DateTime
    isSelected: boolean
    onSelect: () => void
}>

function DayPill({ day, isSelected, onSelect }: DayPillProps) {
    const { classes } = useDayPillStyles({ isSelected })
    return (
        <ButtonBase className={classes.pill} onClick={onSelect}>
            <Typography variant="subtitle1" className={classes.dayNumber}>
                {day.day}
            </Typography>
            <Typography variant="caption" className={classes.dayName}>
                {day.toFormat("EEE")}
            </Typography>
        </ButtonBase>
    )
}

const useStyles = makeStyles()((theme) => ({
    bar: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        padding: ".75rem 1.5rem",
        background: theme.palette.common.white,
        flexShrink: 0,
    },
    spacer: {
        flex: 1,
    },
    daysGroup: {
        display: "flex",
        alignItems: "center",
        background: theme.palette.tailwind.blue[100],
        borderRadius: ".75rem",
        gap: ".125rem",
        overflowX: "auto",
        flexShrink: 0,
        "&::-webkit-scrollbar": {
            display: "none",
        },
    },
    orText: {
        color: theme.palette.tailwind.slate[950],
        flexShrink: 0,
        fontWeight: 400,
    },
}))

type TakeawayDateBarProps = Readonly<{
    selectedDate: DateTime
    onDateChange: (date: DateTime) => void
}>

export function TakeawayDateBar({ selectedDate, onDateChange }: TakeawayDateBarProps) {
    const { classes } = useStyles()
    const dt = useDateTime()

    const today = dt.now()
    const days = Array.from({ length: 5 }, (_, i) => today.plus({ days: i }))

    const isCustomDate = !days.some((d) => d.hasSame(selectedDate, "day"))

    return (
        <Box className={classes.bar}>
            <Box className={classes.daysGroup}>
                {days.map((day) => (
                    <DayPill
                        key={day.toISODate() ?? String(day.valueOf())}
                        day={day}
                        isSelected={!isCustomDate && day.hasSame(selectedDate, "day")}
                        onSelect={() => onDateChange(day)}
                    />
                ))}
            </Box>

            <LocalizedStrict id="takeaway-date-bar-or">
                <Typography variant="subtitle2" className={classes.orText}>
                    or
                </Typography>
            </LocalizedStrict>

            <DatePickerButton selectedDate={selectedDate} isCustomDate={isCustomDate} onDateChange={onDateChange} />

            <Box className={classes.spacer} />
        </Box>
    )
}
